import { addAmounts, fromScaled, toScaled } from '@base/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentParty,
  FactList,
  MiniChart,
  PrintDocument,
  SettingRow,
  SettingsSection,
  StateView,
  StepsLayout,
  Switch,
  WeekAgenda,
} from '../../components/patterns/index.ts';
import {
  Field,
  Input,
  NumCell,
  StatCard,
  Table,
  TotalRow,
  ValueTable,
} from '../../components/ui.tsx';

import {
  BoardExample,
  ConversationExample,
  CustomerRecord,
  GalleryExample,
  ListExample,
} from './pattern-examples-records.tsx';
import { CUSTOMERS, pointsOf, SALES, useMoney, useSales } from './pattern-samples.ts';

/** O exemplo de cada padrão de tela, montado só com as peças de `components/`. */
export function PatternExample({ id }: { id: string }) {
  switch (id) {
    case 'dashboard':
      return <DashboardExample />;
    case 'list':
      return <ListExample />;
    case 'record':
      return <CustomerRecord customer={CUSTOMERS[1]!} />;
    case 'board':
      return <BoardExample />;
    case 'agenda':
      return <AgendaExample />;
    case 'gallery':
      return <GalleryExample />;
    case 'wizard':
      return <WizardExample />;
    case 'conversation':
      return <ConversationExample />;
    case 'report':
      return <ReportExample />;
    case 'settings':
      return <SettingsExample />;
    case 'document':
      return <DocumentExample />;
    default:
      return <LoginNote />;
  }
}

function DashboardExample() {
  const { t } = useTranslation();
  const money = useMoney();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const sales = useSales();
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={s('orders')} value={142} />
        <StatCard label={s('revenue')} value={money(SALES[5])} />
        <StatCard label={s('openTickets')} value={9} />
        <StatCard label={s('lateCount')} value={3} alert />
      </div>
      <div className="ui-panel rounded-lg bg-sheet p-5">
        <MiniChart label={s('salesByMonth')} points={pointsOf(sales)} />
      </div>
    </div>
  );
}

function AgendaExample() {
  const { t, i18n } = useTranslation();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const monday = Date.UTC(2026, 10, 2);
  const days = [0, 1, 2, 3, 4].map((d) =>
    new Date(monday + d * 864e5).toLocaleDateString(i18n.language, {
      weekday: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }),
  );
  const events = [
    { id: 'e1', day: 0, start: 540, end: 600, what: s('visit'), who: 'Ana Ribeiro' },
    { id: 'e2', day: 0, start: 570, end: 660, what: s('meeting'), who: 'Bruno Costa' },
    { id: 'e3', day: 2, start: 840, end: 930, what: s('delivery'), who: 'Carla Mendes' },
    { id: 'e4', day: 3, start: 600, end: 690, what: s('visit'), who: 'Diego Alves' },
  ];
  return (
    <WeekAgenda
      label={s('agendaLabel')}
      days={days}
      today={1}
      events={events}
      renderEvent={(e) => (
        <>
          <span className="block truncate font-medium">{e.what}</span>
          <span className="block truncate opacity-80">{e.who}</span>
        </>
      )}
    />
  );
}

function WizardExample() {
  const { t } = useTranslation();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const [step, setStep] = useState(0);
  const steps = (['data', 'address', 'review'] as const).map((k) => s(`wizardSteps.${k}`));
  return (
    <StepsLayout
      steps={steps}
      current={step}
      onBack={() => setStep((n) => Math.max(0, n - 1))}
      onNext={() => setStep((n) => (n < steps.length - 1 ? n + 1 : 0))}
      finishLabel={s('saveCustomer')}
    >
      {step < 2 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={step === 0 ? s('name') : s('city')} required>
            <Input />
          </Field>
        </div>
      ) : (
        <FactList
          items={[
            { label: s('name'), value: 'Ana Ribeiro' },
            { label: s('city'), value: 'Belo Horizonte' },
          ]}
        />
      )}
    </StepsLayout>
  );
}

function ReportExample() {
  const { t } = useTranslation();
  const money = useMoney();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const sales = useSales();
  const total = addAmounts(...SALES);
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="ui-panel rounded-lg bg-sheet p-5">
        <MiniChart label={s('salesByMonth')} points={pointsOf(sales)} kind="line" />
      </div>
      <ValueTable
        label={s('salesByMonth')}
        head={[s('month'), { label: s('value'), align: 'right' }]}
      >
        {sales.map((p) => (
          <tr key={p.label}>
            <td>{p.label}</td>
            <NumCell>{money(p.value)}</NumCell>
          </tr>
        ))}
        <TotalRow>
          <td>{s('reportTotal')}</td>
          <NumCell>{money(total)}</NumCell>
        </TotalRow>
      </ValueTable>
    </div>
  );
}

function SettingsExample() {
  const { t } = useTranslation();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const [late, setLate] = useState(true);
  const [weekly, setWeekly] = useState(false);
  return (
    <SettingsSection title={s('settingsTitle')} description={s('settingsDescription')}>
      <SettingRow
        label={s('notifyLate')}
        description={s('notifyLateHint')}
        control={(id) => <Switch checked={late} onChange={setLate} labelledBy={id} />}
      />
      <SettingRow
        label={s('weekly')}
        control={(id) => <Switch checked={weekly} onChange={setWeekly} labelledBy={id} />}
      />
    </SettingsSection>
  );
}

function DocumentExample() {
  const { t, i18n } = useTranslation();
  const money = useMoney();
  const s = (k: string) => t(`patternsPage.sample.${k}`);
  const items = [
    ['a', 1, '1800.00'],
    ['b', 12, '350.00'],
    ['c', 2, '600.00'],
  ] as const;
  const lineTotal = (q: number, v: string) => fromScaled(toScaled(v) * BigInt(q));
  const total = addAmounts(...items.map(([, q, v]) => lineTotal(q, v)));
  return (
    <PrintDocument
      title={s('quote')}
      code="ORC-2026-000123"
      issuedAt={new Date(Date.UTC(2026, 10, 3)).toLocaleDateString(i18n.language, {
        timeZone: 'UTC',
      })}
      parties={
        <>
          <DocumentParty label={s('from')} lines={['Marina Souza', 'Belo Horizonte']} />
          <DocumentParty label={s('to')} lines={['Ana Ribeiro', 'Belo Horizonte']} />
        </>
      }
      footer={s('notes')}
    >
      <Table
        head={[
          s('item'),
          { label: s('qty'), align: 'right' },
          { label: s('total'), align: 'right' },
        ]}
      >
        {items.map(([k, q, v]) => (
          <tr key={k}>
            <td>{s(`items.${k}`)}</td>
            <NumCell>{q}</NumCell>
            <NumCell>{money(lineTotal(q, v))}</NumCell>
          </tr>
        ))}
        <tr>
          <td className="font-semibold">{s('total')}</td>
          <td />
          <td className="text-right font-semibold tabular-nums">{money(total)}</td>
        </tr>
      </Table>
    </PrintDocument>
  );
}

function LoginNote() {
  const { t } = useTranslation();
  return (
    <div className="ui-panel rounded-lg bg-sheet">
      <StateView kind="empty" compact title={t('patternsPage.loginNote')} />
    </div>
  );
}
